import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  makeStyles,
  Box,
  Checkbox,
  Button,
  Typography
} from '@material-ui/core';
import {
  Visibility,
  LocationOn,
  Image,
  CheckCircle,
  Warning,
  Schedule,
  CameraAlt,
  Opacity,
  GetApp
} from '@material-ui/icons';
import { format } from 'date-fns';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650
  },
  tableContainer: {
    maxHeight: 600
  },
  statusChip: {
    fontWeight: 'bold'
  },
  deadChip: {
    backgroundColor: '#ffcdd2',
    color: '#b71c1c'
  },
  dyingChip: {
    backgroundColor: '#ffe0b2',
    color: '#e65100'
  },
  identifiedChip: {
    backgroundColor: '#e3f2fd',
    color: '#0d47a1'
  },
  removedChip: {
    backgroundColor: '#f3e5f5',
    color: '#4a148c'
  },
  completedChip: {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20'
  },
  pendingChip: {
    backgroundColor: '#fff9c4',
    color: '#f57f17'
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100]
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5)
  },
  quantityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderRadius: '50%',
    width: 24,
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 'bold'
  }
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'select', label: '', sortable: false },
  { id: 'plantId', label: 'Plant ID', sortable: true },
  { id: 'type', label: 'Type', sortable: true },
  { id: 'state', label: 'Status', sortable: true },
  { id: 'quantity', label: 'Qty', sortable: true },
  { id: 'removalStatus', label: 'Removal', sortable: false },
  { id: 'jobSiteName', label: 'Job Site', sortable: true },
  { id: 'clientName', label: 'Client', sortable: true },
  { id: 'crewName', label: 'Crew', sortable: true },
  { id: 'reportedByName', label: 'Reported By', sortable: true },
  { id: 'dateReported', label: 'Date Reported', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false }
];

export default function PlantTrackingTable({ plants, onPlantClick, loading, apiUrl }) {
  const classes = useStyles();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('dateReported');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);
  
  console.log('PlantTrackingTable received plants:', plants);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = plantsArray.map((n) => n._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const getStatusChip = (type, state) => {
    let chipClass = '';
    let label = state;
    let icon = null;

    // Handle both uppercase and lowercase formats
    const normalizedState = state.toUpperCase();
    
    switch (normalizedState) {
      case 'IDENTIFIED':
        chipClass = classes.identifiedChip;
        icon = <Schedule fontSize="small" />;
        label = 'Identified';
        break;
      case 'IMAGE_CAPTURED':
        chipClass = classes.pendingChip;
        icon = <CameraAlt fontSize="small" />;
        label = 'Image Captured';
        break;
      case 'LOCATED':
        chipClass = classes.pendingChip;
        icon = <LocationOn fontSize="small" />;
        label = 'Located';
        break;
      case 'REMOVED':
        chipClass = classes.removedChip;
        icon = <CheckCircle fontSize="small" />;
        label = 'Removed';
        break;
      case 'IRRIGATION_CONFIRMED':
        chipClass = classes.pendingChip;
        icon = <Opacity fontSize="small" />;
        label = 'Irrigation Confirmed';
        break;
      case 'COMPLETED':
        chipClass = classes.completedChip;
        icon = <CheckCircle fontSize="small" />;
        label = 'Completed';
        break;
      default:
        chipClass = classes.pendingChip;
        icon = <Warning fontSize="small" />;
        label = state;
    }

    return (
      <Chip
        size="small"
        label={label}
        className={`${classes.statusChip} ${chipClass}`}
        icon={icon}
      />
    );
  };

  const getTypeChip = (type) => {
    const chipClass = type === 'dead' ? classes.deadChip : classes.dyingChip;
    return (
      <Chip
        size="small"
        label={type}
        className={`${classes.statusChip} ${chipClass}`}
      />
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'MM/dd/yyyy');
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${apiUrl}/api/hmi/plant-tracking/export`, {
        responseType: 'blob',
        params: {
          // You could pass current filters here if needed
        }
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `plant_tracking_export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  // Ensure plants is an array
  const plantsArray = Array.isArray(plants) ? plants : [];
  
  const processedPlants = plantsArray.map(plant => ({
    ...plant,
    plantId: plant.plantId || plant._id?.substring(0, 8),
    jobSiteName: plant.jobSite?.name || '-',
    clientName: plant.client?.displayName || '-',
    crewName: plant.crew?.name || '-',
    dateReported: plant.timestamps?.created || plant.createdAt
  }));

  const sortedPlants = stableSort(processedPlants, getComparator(order, orderBy));
  const paginatedPlants = sortedPlants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper>
      <Box display="flex" justifyContent="flex-end" p={2}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetApp />}
          onClick={handleExport}
          disabled={exporting || plantsArray.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </Box>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" className={classes.headerCell}>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < plantsArray.length}
                  checked={plantsArray.length > 0 && selected.length === plantsArray.length}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              {headCells.slice(1).map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.id === 'quantity' ? 'center' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  className={classes.headerCell}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPlants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center">
                  <Box py={4}>
                    <Typography variant="body1" color="textSecondary">
                      No plant tracking data found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPlants.map((plant) => {
              const isItemSelected = isSelected(plant._id);
              
              return (
                <TableRow
                  hover
                  key={plant._id}
                  selected={isItemSelected}
                  className={classes.clickableRow}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClick(event, plant._id);
                      }}
                    />
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {plant.plantId}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {getTypeChip(plant.type)}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {getStatusChip(plant.type, plant.state)}
                  </TableCell>
                  <TableCell align="center" onClick={() => onPlantClick(plant)}>
                    <Box className={classes.quantityBadge}>
                      {plant.quantity || 1}
                    </Box>
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {plant.removalStatus && plant.isRemoving && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title={plant.removalStatus.needsAfterPhoto ? "Needs after photo" : "Has after photo"}>
                          <CameraAlt 
                            fontSize="small" 
                            style={{ 
                              color: plant.removalStatus.hasAfterPhoto ? '#4caf50' : '#f44336' 
                            }} 
                          />
                        </Tooltip>
                        <Tooltip title={plant.removalStatus.needsIrrigationDetails ? "Needs irrigation details" : "Has irrigation details"}>
                          <Opacity 
                            fontSize="small" 
                            style={{ 
                              color: plant.removalStatus.hasIrrigationDetails ? '#4caf50' : '#f44336' 
                            }} 
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {plant.jobSiteName}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {plant.client?.displayName}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {plant.crewName}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {plant.reportedByName || '-'}
                  </TableCell>
                  <TableCell onClick={() => onPlantClick(plant)}>
                    {formatDate(plant.dateReported)}
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actionButtons}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlantClick(plant);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {plant.location && (
                        <Tooltip title="View on Map">
                          <IconButton size="small">
                            <LocationOn fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(plant.photos?.before || plant.photos?.after) && (
                        <Tooltip title="View Photos">
                          <IconButton size="small">
                            <Image fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={plantsArray.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Paper>
  );
}